import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Download, Eye, Edit, Building } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePagination, useFilters, useSearch, useCrud } from '../../hooks/useApi';
import { catalogService } from '../../services/api';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import CompanySizeModal from '../../components/Catalog/CompanySizeModal';
import CompanySizeTable from '../../components/Catalog/CompanySizeTable';
import CompanySizeFilters from '../../components/Catalog/CompanySizeFilters';
import { downloadJson } from '../../utils/exportHelpers';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { isEmptyRichText } from '../../components/RichTextEditor';

function mapApiToUi(row) {
  return {
    id: row.id,
    name: row.name,
    description: row.description || '',
    minEmployees: row.min_employees ?? row.minEmployees,
    maxEmployees: row.max_employees ?? row.maxEmployees,
    isActive: row.is_active ?? row.isActive,
    createdAt: row.created_at || row.createdAt || new Date().toISOString(),
  };
}

function toPayload(data) {
  const max = data.maxEmployees === '' || data.maxEmployees == null ? null : Number(data.maxEmployees);
  return {
    name: data.name,
    description: isEmptyRichText(data.description) ? null : data.description,
    min_employees: Number(data.minEmployees),
    max_employees: max,
    is_active: data.isActive === 'true' || data.isActive === true,
  };
}

const CompanySizes = () => {
  const { t } = useTranslation('catalog');

  const [showModal, setShowModal] = useState(false);
  const [selectedCompanySize, setSelectedCompanySize] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  const pagination = usePagination(1, 10);
  const filters = useFilters({
    name: '',
    isActive: '',
  });
  const search = useSearch();

  const { useList, useCreate, useUpdate, useDelete } = useCrud('company-sizes', catalogService.companySizeCrud);
  const { data: listData, isLoading, error, refetch } = useList();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const rawList = useMemo(() => {
    const d = listData;
    const arr = Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
    return arr;
  }, [listData]);

  const mapped = useMemo(() => rawList.map(mapApiToUi), [rawList]);

  const filtered = useMemo(() => {
    let rows = mapped;
    const n = (filters.filters.name || '').trim().toLowerCase();
    if (n) rows = rows.filter((r) => (r.name || '').toLowerCase().includes(n));
    if (filters.filters.isActive === 'true') rows = rows.filter((r) => r.isActive === true);
    if (filters.filters.isActive === 'false') rows = rows.filter((r) => r.isActive === false);
    const q = (search.query || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [mapped, filters.filters.name, filters.filters.isActive, search.query]);

  useEffect(() => {
    pagination.setTotal(filtered.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  const paged = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filtered.slice(start, start + pagination.limit);
  }, [filtered, pagination.page, pagination.limit]);

  const handleCreateCompanySize = () => {
    setSelectedCompanySize(null);
    setShowModal(true);
  };

  const handleEditCompanySize = (companySize) => {
    setSelectedCompanySize(companySize);
    setShowModal(true);
  };

  const handleViewCompanySize = (companySize) => {
    toast(
      [
        `Nome: ${companySize.name}`,
        `Funcionários: ${companySize.minEmployees} – ${companySize.maxEmployees ?? '∞'}`,
        `Ativo: ${companySize.isActive ? 'Sim' : 'Não'}`,
        companySize.description ? `Descrição: ${companySize.description}` : '',
      ]
        .filter(Boolean)
        .join('\n'),
      { duration: 6000 }
    );
  };

  const handleDeleteCompanySize = (companySize) => {
    if (!window.confirm('Excluir este tamanho de empresa?')) return;
    deleteMutation.mutate(companySize.id, {
      onSuccess: () => {
        setShowModal(false);
        setSelectedCompanySize(null);
      },
      onError: (e) => toast.error(userFacingError(e, 'deleteError')),
    });
  };

  const handleSave = async (data) => {
    const payload = toPayload(data);
    if (selectedCompanySize) {
      await updateMutation.mutateAsync({ id: selectedCompanySize.id, data: payload });
      setShowModal(false);
      setSelectedCompanySize(null);
      return;
    }
    await createMutation.mutateAsync(payload);
    setShowModal(false);
  };

  const handleExport = () => {
    downloadJson(`tamanhos-empresa-${new Date().toISOString().slice(0, 10)}.json`, filtered);
    toast.success('Exportação iniciada');
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>
        {isActive ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  if (error) {
    return (
      <FormPageShell>
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-danger-800">
          {t('common:loadError')}{' '}
          <button type="button" className="btn btn-outline btn-sm ml-2" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("sizesTitle")}
          description="Porte por faixa de funcionários (ME, EPP, etc.)."
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Catálogo' }]}
        >
          <Can permission={P.CATALOG_ACCESS}>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button type="button" className="btn btn-outline shadow-sm" onClick={handleExport} disabled={!filtered.length}>
                <Download className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Exportar</span>
              </button>
              <button
                type="button"
                onClick={handleCreateCompanySize}
                className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
              >
                <Plus className="h-4 w-4" />
                Novo tamanho
              </button>
            </div>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <CompanySizeFilters filters={filters} search={search} />
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            {isLoading && (
              <p className="text-sm text-gray-600" role="status">
                Carregando…
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Mostrando {filtered.length} tamanho(s)
              </div>
            </div>

            {viewMode === 'table' ? (
              <CompanySizeTable
                companySizes={paged}
                totalCount={filtered.length}
                pagination={pagination}
                onView={handleViewCompanySize}
                onEdit={handleEditCompanySize}
                onDelete={handleDeleteCompanySize}
                getStatusBadge={getStatusBadge}
                onCreate={handleCreateCompanySize}
              />
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {paged.map((companySize) => (
                  <div key={companySize.id} className="card">
                    <div className="card-content">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                            <Building className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{companySize.name}</h3>
                            <p className="text-sm text-gray-600">{companySize.description}</p>
                          </div>
                        </div>
                        {getStatusBadge(companySize.isActive)}
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Funcionários:</span>
                          <span className="text-gray-900">
                            {companySize.minEmployees} - {companySize.maxEmployees || '∞'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Criado em:</span>
                          <span className="text-gray-900">
                            {new Date(companySize.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>

                      <Can permission={P.CATALOG_ACCESS}>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewCompanySize(companySize)}
                            className="btn btn-outline btn-sm flex-1"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditCompanySize(companySize)}
                            className="btn btn-outline btn-sm flex-1"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </button>
                        </div>
                      </Can>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <CompanySizeModal
          key={selectedCompanySize?.id ?? 'new'}
          companySize={selectedCompanySize}
          onClose={() => {
            setShowModal(false);
            setSelectedCompanySize(null);
          }}
          onSave={handleSave}
        />
      )}
    </FormPageShell>
  );
};

export default CompanySizes;
